import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Bell } from 'react-feather';
import { ShepherdTourContext } from 'react-shepherd';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { useLocation, useParams } from 'react-router';
import { useMessages } from '@hooks/useMessages';
import reservationsApi from '@data/api/reservations';
import { useCompany } from '@data/store/CompanyProvider';
import { toastOnError } from '@data/utility/errors';
import { formatName } from '@data/utility/names';
import { ReservationStatus } from '@data/types/clients';
import { CalendarType } from '@data/types/calendars';
import { GetActionableReservationsResponse } from '@data/types/reservations';
import { ReservationItem } from './reservation-item';
import { toApiIsoDate } from '@data/utility/dates';
import { useNotifications } from '@data/store/NotificationsProvider';
import { useAuth } from '@data/store/AuthProvider';

interface SharedParsedAlert {
  calendarTitle: string;
  startDate: string;
  endDate: string;
  serviceTitle: string;
  timeZone: string;
  id: string;
  slotId?: string;
  shared: boolean;
}

interface ParsedTimeAlert extends SharedParsedAlert {
  clientName: string;
  type: CalendarType.Time;
  status: ReservationStatus;
}

interface ParsedSlotAlert extends SharedParsedAlert {
  type: CalendarType.Slot;
  spotsText: string;
}

type ParsedAlert = ParsedTimeAlert | ParsedSlotAlert;

const parseActionableReservations = (
  res: GetActionableReservationsResponse
): ParsedAlert[] => {
  return [
    ...res.reservations.map(
      ({ id, clientFirstName, clientLastName, shared, ...rest }) => ({
        ...rest,
        id,
        clientName: formatName(clientFirstName, clientLastName),
        type: CalendarType.Time as const,
        shared,
      })
    ),
    ...res.slots.map(({ spotsCount, spotsTaken, id, ...rest }) => ({
      ...rest,
      id,
      slotId: id,
      type: CalendarType.Slot as const,
      shared: false,
      spotsText: `${spotsTaken}/${spotsCount}`,
    })),
  ].sort((r1, r2) => r1.startDate.localeCompare(r2.startDate));
};

const AlertsDropdown: React.FC = () => {
  const { messages } = useMessages();
  const { companyId: urlCompanyId } = useParams<{ companyId?: string }>();
  const { selectedCompanyId } = useCompany();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const tour = useContext(ShepherdTourContext);
  const { shouldShowPermission, notificationsCounter } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState<ParsedAlert[]>([]);
  const [tourHasStarted, setTourHasStarted] = useState(false);
  const today = toApiIsoDate(new Date());
  const pendingAlerts = useMemo(
    () =>
      alerts.filter(
        (a) => (a as ParsedTimeAlert).status === ReservationStatus.Pending
      ),
    [alerts]
  );
  const oldAlerts = useMemo(
    () =>
      alerts.filter(
        (a) =>
          a.startDate.localeCompare(today) < 0 &&
          (a as ParsedTimeAlert).status !== ReservationStatus.Pending
      ),
    [alerts, today]
  );
  const upcomingAlerts = useMemo(
    () =>
      alerts.filter(
        (a) =>
          a.startDate.localeCompare(today) > 0 &&
          (a as ParsedTimeAlert).status !== ReservationStatus.Pending
      ),
    [alerts, today]
  );
  const badge = useMemo(
    () => pendingAlerts.length + oldAlerts.length,
    [pendingAlerts, oldAlerts]
  );

  const toggleIsOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (
      !selectedCompanyId ||
      (urlCompanyId && urlCompanyId !== selectedCompanyId) ||
      !user
    ) {
      return;
    }

    reservationsApi
      .getActionableReservations(selectedCompanyId)
      .then((res) => {
        setAlerts(parseActionableReservations(res));
      })
      .catch(toastOnError);
    // update alerts when url changes or notification is received
  }, [selectedCompanyId, pathname, notificationsCounter, urlCompanyId, user]);

  useEffect(() => {
    if (
      !shouldShowPermission ||
      !tour ||
      !selectedCompanyId ||
      tourHasStarted
    ) {
      return;
    }
    setTourHasStarted(true);
    tour.start();
  }, [selectedCompanyId, shouldShowPermission, tour, tourHasStarted]);

  if (!selectedCompanyId) {
    return null;
  }

  return (
    <Dropdown
      tag="li"
      className="dropdown-notification nav-item mr-sm-1"
      isOpen={isOpen}
      toggle={toggleIsOpen}
      id="alerts-dropdown"
    >
      <DropdownToggle
        color="transparent"
        className="d-flex align-items-center px-50 py-0"
      >
        <div className="px-50 d-flex position-relative">
          <Bell size={21} />
          {!!badge && (
            <span className="badge rounded-pill bg-danger badge-up">
              {badge > 999 ? '999+' : badge}
            </span>
          )}
        </div>
        <h5 className="ml-50 mb-0 dropdown-btn-text">
          {messages('nav_header_alerts')}
        </h5>
      </DropdownToggle>
      <DropdownMenu
        className="mt-0"
        style={{
          maxHeight: '400px',
          width: '400px',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
        right
      >
        {!!badge && (
          <>
            <DropdownItem header tag="div" className="pt-1">
              <span className="h5 font-weight-bolder">
                {messages(
                  'alerts_dropdown_header_reservations_action_required'
                )}
              </span>
            </DropdownItem>
            <DropdownItem divider />
            {pendingAlerts.map((reservation) => {
              return (
                <DropdownItem key={reservation.id} style={{ width: '100%' }}>
                  <ReservationItem
                    reservation={reservation}
                    companyId={selectedCompanyId}
                    spotInfo={
                      reservation.type === CalendarType.Time
                        ? reservation.clientName
                        : reservation.spotsText
                    }
                  />
                </DropdownItem>
              );
            })}
            {oldAlerts.map((reservation) => {
              return (
                <DropdownItem key={reservation.id} style={{ width: '100%' }}>
                  <ReservationItem
                    reservation={reservation}
                    companyId={selectedCompanyId}
                    spotInfo={
                      reservation.type === CalendarType.Time
                        ? reservation.clientName
                        : reservation.spotsText
                    }
                  />
                </DropdownItem>
              );
            })}
          </>
        )}
        {upcomingAlerts.length > 0 && !!badge && <DropdownItem divider />}
        {upcomingAlerts.length > 0 && (
          <>
            <DropdownItem header tag="div">
              <span className="h5 font-weight-bolder">
                {messages('alerts_dropdown_header_reservations_upcoming')}
              </span>
            </DropdownItem>
            <DropdownItem divider />
            {upcomingAlerts.map((reservation) => {
              return (
                <DropdownItem key={reservation.id} style={{ width: '100%' }}>
                  <ReservationItem
                    reservation={reservation}
                    companyId={selectedCompanyId}
                    spotInfo={
                      reservation.type === CalendarType.Time
                        ? reservation.clientName
                        : reservation.spotsText
                    }
                  />
                </DropdownItem>
              );
            })}
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

export default AlertsDropdown;
